/*
 * product-ijewel-cad-resolver.js — on-demand cad_code → GLB resolution.
 *
 * Layer B of the iJewel model map. The PDP no longer ships a full
 * variant → GLB map: `metaobjects.<type>[handle]` resolves at most ~20 DISTINCT
 * entries per storefront request, so any product with more than 20 distinct CAD
 * codes (Georgian Pinky Ring: 50, necklaces: 40) had its map silently truncated
 * and every shape past the 20th simply had no model.
 *
 * Instead:
 *   - Liquid ships `window.iJewelVariantCadMap` = { variantId: cadHandle } (no
 *     metaobject lookups — no CAD ceiling, but capped at the first 250
 *     VARIANTS by `product.variants`; product-ijewel-bridge.js merges the
 *     remainder from the paginated `?view=variants` matrix — see HEI-205),
 *     plus the selected variant's resolved entry in `window.iJewelVariantMap`
 *     (one lookup).
 *   - This module resolves any OTHER cad on demand through
 *     `/products/{handle}?view=ijewel-cad&variant={id}`, which resolves exactly
 *     one metaobject (~240 bytes).
 *
 * Cache is keyed by CAD, never by variant id: a shape's 4 stone-type siblings
 * share one geometry, so they must share one request. On success the resolved
 * URL is written onto EVERY variant carrying that cad, which is what keeps
 * product-ijewel-bridge.js and product-ijewel-prefetch.js working against the
 * `window.iJewelVariantMap` shape they already understand.
 *
 * Public API (window.IjewelCadResolver):
 *   resolve(cad, variantIdHint) → Promise<{cad, url}>   cached + in-flight deduped
 *   peek(cad)                   → {cad, url} | null     no network
 *   inFlight(cad)               → boolean
 *   cadForVariant(variantId)    → cad handle | ''
 *   variantForCad(cad)          → variantId | ''
 *   recommit()                  → re-apply known URLs onto the current cad map
 *                                 (combined-listing metal switch: cad codes and
 *                                  GLBs are shared across metal siblings, only
 *                                  variant ids change, so nothing re-downloads)
 */
// @ts-nocheck — vanilla JS over custom window.* globals, as the bridge.
(function () {
  'use strict';

  var VIEW = 'ijewel-cad';

  var cadPromises = {}; // cad -> Promise<{cad, url}>  (in-flight + settled)
  var cadUrls = {}; // cad -> resolved url

  function cadMap() {
    return window.iJewelVariantCadMap || {};
  }

  function resolvedMap() {
    if (!window.iJewelVariantMap) window.iJewelVariantMap = {};
    return window.iJewelVariantMap;
  }

  // Mirrors product-ijewel-bridge.js normalizeAssetUrl: protocol-relative or
  // http:// URLs break Safari's CORS follow of the CDN's https redirect.
  function normalizeAssetUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('//') === 0) return 'https:' + url;
    if (url.indexOf('http://') === 0) return 'https://' + url.slice(7);
    return url;
  }

  function productHandle() {
    var root = document.querySelector('[data-ijewel-root]');
    if (root && root.dataset.productHandle) return root.dataset.productHandle;
    var json = document.querySelector('.product-purchase-form__product-json');
    return (json && json.dataset && json.dataset.productHandle) || '';
  }

  function cadForVariant(variantId) {
    if (!variantId) return '';
    var id = String(variantId);
    var entry = resolvedMap()[id];
    if (entry && entry.cad) return entry.cad;
    return cadMap()[id] || '';
  }

  /** Any variant id carrying this cad — the resolver only needs one. */
  function variantForCad(cad) {
    if (!cad) return '';
    var lightweight = cadMap();
    var ids = Object.keys(lightweight);
    for (var i = 0; i < ids.length; i++) {
      if (lightweight[ids[i]] === cad) return ids[i];
    }
    // Fallback for a product whose Layer A map is missing (older cached HTML).
    var resolved = resolvedMap();
    ids = Object.keys(resolved);
    for (var j = 0; j < ids.length; j++) {
      if (resolved[ids[j]] && resolved[ids[j]].cad === cad) return ids[j];
    }
    return '';
  }

  /* Write a resolved url onto every variant sharing this cad. This is what
     makes "4 stone types = 1 request" true, and what lets the bridge and
     prefetcher keep reading window.iJewelVariantMap unchanged. */
  function commit(cad, url) {
    cadUrls[cad] = url;
    var resolved = resolvedMap();
    var lightweight = cadMap();
    Object.keys(lightweight).forEach(function (id) {
      if (lightweight[id] !== cad) return;
      var prev = resolved[id];
      if (!prev || prev.url !== url || prev.cad !== cad) {
        resolved[id] = { url: url, cad: cad };
      }
    });
    return url;
  }

  function recommit() {
    Object.keys(cadUrls).forEach(function (cad) {
      commit(cad, cadUrls[cad]);
    });
  }

  function peek(cad) {
    if (cad && cadUrls[cad]) return { cad: cad, url: cadUrls[cad] };
    return null;
  }

  function inFlight(cad) {
    return !!(cad && cadPromises[cad]);
  }

  /**
   * Resolve one cad to its GLB url. Cached and in-flight-deduped by cad, so
   * A → 1 request, B → 1 request, A again → 0 requests, C twice concurrently
   * → 1 request.
   */
  function resolve(cad, variantIdHint) {
    if (!cad) return Promise.reject(new Error('iJewel CAD resolver: no cad given'));
    if (cadPromises[cad]) return cadPromises[cad];

    var handle = productHandle();
    // Prefer a hint whose cad actually matches — a stale hint would resolve the
    // wrong model, and the response check below would then reject it.
    var variantId = '';
    if (variantIdHint && cadForVariant(variantIdHint) === cad) variantId = String(variantIdHint);
    if (!variantId) variantId = variantForCad(cad);

    if (!handle || !variantId) {
      console.warn('iJewel 3D: cannot resolve CAD — no product handle or variant for it', {
        cad: cad,
        handle: handle || '(none)',
        variantId: variantId || '(none)',
      });
      return Promise.reject(new Error('iJewel CAD resolver: no handle/variant for ' + cad));
    }

    var endpoint =
      '/products/' +
      encodeURIComponent(handle) +
      '?view=' +
      VIEW +
      '&variant=' +
      encodeURIComponent(variantId);

    var promise = fetch(endpoint, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var map = (data && data.map) || {};
        var keys = Object.keys(map);
        if (!keys.length) throw new Error('no model for this variant (empty map)');

        var entry = map[keys[0]];
        if (!entry || !entry.url) throw new Error('entry has no GLB url');
        if (entry.cad !== cad) {
          throw new Error('cad mismatch — asked "' + cad + '", got "' + entry.cad + '"');
        }
        if (String(data.variant) !== String(variantId)) {
          throw new Error(
            'variant mismatch — asked ' + variantId + ', server selected ' + data.variant
          );
        }

        var url = commit(cad, normalizeAssetUrl(entry.url));
        document.dispatchEvent(
          new CustomEvent('ijewel:cad-resolved', {
            detail: { cad: cad, url: url, variantId: variantId },
          })
        );
        return { cad: cad, url: url };
      })
      .catch(function (error) {
        // Never cache a failure — a later selection or warm may legitimately retry.
        delete cadPromises[cad];
        console.warn('iJewel 3D: CAD resolution failed', {
          cad: cad,
          variantId: variantId,
          endpoint: endpoint,
          reason: (error && error.message) || String(error),
        });
        throw error;
      });

    cadPromises[cad] = promise;
    return promise;
  }

  window.IjewelCadResolver = {
    resolve: resolve,
    peek: peek,
    inFlight: inFlight,
    cadForVariant: cadForVariant,
    variantForCad: variantForCad,
    recommit: recommit,
  };
})();
